/**
 * BRXFlowEnhancements
 *
 * Enhancement rules applied to the active Embeddables flow.
 * Knows nothing about overlay, loader, or boot sequencing.
 *
 * Usage:
 *   var ctx = window.BRXFlowEnhancements.create({ savvyId, sectionRoot });
 *   ctx.start();           // attaches click handler + session subscription
 *   ctx.enhancements;      // array passed to BRXFlowRuntime.start()
 *   ctx.activeFlowRef;     // { current: flowEl } — kept in sync by BRXFlowRuntime
 *   ctx.stop();            // removes listeners / unsubscribes
 */
(function () {
    var TERMS_URL = 'https://braverx.com/pages/terms-of-service';
    var PRIVACY_URL = 'https://braverx.com/pages/privacy-policy';
    var TELEHEALTH_URL = 'https://braverx.com/pages/telehealth-consent';

    // ==================================================
    // Flow context helpers
    // ==================================================

    function getFlowPageId(flowRoot) {
        return flowRoot ? (flowRoot.getAttribute('data-flow-page-id-value') || '') : '';
    }

    function getFlowPageKey(flowRoot) {
        if (!flowRoot) return '';
        var pageEl = flowRoot.querySelector('[data-controller~="page"]');
        return pageEl ? (pageEl.getAttribute('data-page-page-key-value') || '') : '';
    }

    // matcher: { pageKey, pageId } — pageKey is primary; pageId is fallback when pageKey absent
    function isFlowPage(flowRoot, matcher) {
        if (!flowRoot || !matcher) return false;
        var pageKey = getFlowPageKey(flowRoot);
        var pageId = getFlowPageId(flowRoot);
        if (matcher.pageKey) {
            if (pageKey === matcher.pageKey) return true;
            if (!pageKey && matcher.pageId && pageId === matcher.pageId) return true;
            return false;
        }
        if (matcher.pageId) return pageId === matcher.pageId;
        return false;
    }

    // finder: { componentKey, pageKey }
    function findFlowComponent(flowRoot, finder) {
        if (!flowRoot || !finder) return null;
        var sel = '';
        if (finder.componentKey) sel += '[data-component-key-value="' + finder.componentKey + '"]';
        if (finder.pageKey) sel += '[data-component-page-key-value="' + finder.pageKey + '"]';
        return sel ? flowRoot.querySelector(sel) : null;
    }

    function markPatched(el, patchKey) {
        if (el) el.setAttribute('data-patched-' + patchKey, 'true');
    }

    function isPatched(el, patchKey) {
        return el ? el.getAttribute('data-patched-' + patchKey) === 'true' : false;
    }

    function bindLinksNoBubble(root) {
        if (!root) return;
        var links = root.querySelectorAll('a');
        for (var i = 0; i < links.length; i++) {
            links[i].addEventListener('click', function (e) { e.stopPropagation(); });
        }
    }

    // Calls callback(el) once the element appears; disconnects after timeoutMs.
    function waitForElement(flowRoot, finder, timeoutMs, callback) {
        var found = finder(flowRoot);
        if (found) { callback(found); return; }
        var obs = new MutationObserver(function () {
            var el = finder(flowRoot);
            if (!el) return;
            obs.disconnect();
            clearTimeout(tid);
            callback(el);
        });
        obs.observe(flowRoot, { childList: true, subtree: true });
        var tid = setTimeout(function () { obs.disconnect(); }, timeoutMs || 3000);
    }

    // ==================================================
    // Checkout button helpers
    // ==================================================

    function getCheckoutState() {
        return window.BRXSession && window.BRXSession.getState
            ? (window.BRXSession.getState().checkout || null)
            : null;
    }

    function getCheckoutReadyButton(flowRoot) {
        return flowRoot
            ? flowRoot.querySelector('[data-component-key-value="go_to_checkout_button"]')
            : null;
    }

    function applyCheckoutReadyButtonState(flowRoot) {
        if (!flowRoot) return false;
        var button = getCheckoutReadyButton(flowRoot);
        if (!button) return false;
        var textEl = button.querySelector('[data-components--custom-button-target="text"]');
        var checkout = getCheckoutState();

        if (!checkout) {
            button.dataset.checkoutState = 'missing';
            button.setAttribute('aria-disabled', 'true');
            button.style.opacity = '0.6';
            button.style.pointerEvents = 'auto';
            if (textEl) textEl.textContent = 'Complete Intake';
            return true;
        }

        if (checkout.isComplete && checkout.checkoutUrl && !checkout.isStale) {
            button.dataset.checkoutState = 'valid';
            button.removeAttribute('aria-disabled');
            button.style.opacity = '';
            button.style.pointerEvents = '';
            if (textEl) textEl.textContent = 'Go to Checkout';
            return true;
        }

        if (checkout.isComplete && checkout.isStale) {
            button.dataset.checkoutState = 'stale';
            button.setAttribute('aria-disabled', 'true');
            button.style.opacity = '0.6';
            button.style.pointerEvents = 'auto';
            if (textEl) textEl.textContent = 'Checkout Needs Refresh';
            return true;
        }

        button.dataset.checkoutState = 'incomplete';
        button.setAttribute('aria-disabled', 'true');
        button.style.opacity = '0.6';
        button.style.pointerEvents = 'auto';
        if (textEl) textEl.textContent = 'Complete Intake';
        return true;
    }

    // ==================================================
    // Factory
    // ==================================================

    function create(options) {
        var savvyId = (options && options.savvyId) || '';
        var sectionRoot = (options && options.sectionRoot) || null;
        var firedFlowEvents = {};
        var checkoutSessionUnsubscribe = null;

        // Shared ref updated by BRXFlowRuntime so the session subscription can
        // reach the current flow element without a closure stale-ref problem.
        var activeFlowRef = { current: null };

        // --------------------------------------------------
        // Enhancement rules
        // --------------------------------------------------

        var consentLinksEnhancement = {
            key: 'consent-links',
            when: function (ctx) {
                return isFlowPage(ctx.flowRoot, { pageKey: 'email_consent' });
            },
            run: function (ctx) {
                var PATCH_KEY = 'consent-links';
                function findTextEl(root) {
                    var comp = findFlowComponent(root, { componentKey: 'email_consent_notice', pageKey: 'email_consent' });
                    return comp ? comp.querySelector('.ElementType-OptionButtonCardText[data-custom-html-text="true"]') : null;
                }
                function applyPatch(textEl) {
                    if (isPatched(textEl, PATCH_KEY)) return;
                    textEl.innerHTML =
                        'I agree to the ' +
                        '<a class="tw-text-angel-green tw-font-medium tw-underline" href="' + TERMS_URL + '" target="_blank" rel="noopener noreferrer">Terms of Service</a>, ' +
                        '<a class="tw-text-angel-green tw-font-medium tw-underline" href="' + PRIVACY_URL + '" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, and ' +
                        '<a class="tw-text-angel-green tw-font-medium tw-underline" href="' + TELEHEALTH_URL + '" target="_blank" rel="noopener noreferrer">Telehealth Consent</a>,' +
                        ' and I understand my responses will be reviewed a US-licensed physician.';
                    markPatched(textEl, PATCH_KEY);
                    bindLinksNoBubble(textEl);
                }
                waitForElement(ctx.flowRoot, findTextEl, 3000, applyPatch);
            }
        };

        var contactInfoConsentEnhancement = {
            key: 'contact-info-consent-links',
            when: function (ctx) {
                return isFlowPage(ctx.flowRoot, { pageKey: 'contact_info' });
            },
            run: function (ctx) {
                var PATCH_KEY = 'contact-info-consent-links';
                function findTextEl(root) {
                    var comp = findFlowComponent(root, { componentKey: 'contact_info_consent', pageKey: 'contact_info' });
                    return comp ? comp.querySelector('.ElementType-OptionButtonCardText[data-custom-html-text="true"]') : null;
                }
                function applyPatch(textEl) {
                    if (isPatched(textEl, PATCH_KEY)) return;
                    textEl.innerHTML =
                        'By providing your phone number, you agree to receive recurring automated text messages from BraveRX. ' +
                        'Msg &amp; data rates may apply. Reply HELP for help, STOP to cancel. View our ' +
                        '<a class="tw-text-angel-green tw-font-medium tw-underline" href="' + PRIVACY_URL + '" target="_blank" rel="noopener noreferrer">Privacy Policy</a>' +
                        ' and ' +
                        '<a class="tw-text-angel-green tw-font-medium tw-underline" href="' + TERMS_URL + '" target="_blank" rel="noopener noreferrer">Terms of Service</a>.';
                    markPatched(textEl, PATCH_KEY);
                    bindLinksNoBubble(textEl);
                }
                waitForElement(ctx.flowRoot, findTextEl, 3000, applyPatch);
            }
        };

        var checkoutReadyEnhancement = {
            key: 'checkout-ready',
            when: function (ctx) {
                return isFlowPage(ctx.flowRoot, { pageKey: 'checkout_ready' });
            },
            run: function (ctx) {
                waitForElement(ctx.flowRoot, getCheckoutReadyButton, 3000, function () {
                    applyCheckoutReadyButtonState(ctx.flowRoot);
                });
            }
        };

        var analyticsPageViewEnhancement = {
            key: 'analytics-page-view',
            when: function (ctx) {
                return !!ctx.pageId && ctx.pageId !== ctx.previousPageId;
            },
            run: function (ctx) {
                if (!window.dataLayer) return;
                window.dataLayer.push({
                    event: 'flow_page_view',
                    flow_page_id: ctx.pageId,
                    flow_page_key: ctx.pageKey,
                    flow_previous_page_id: ctx.previousPageId || null,
                    flow_reason: ctx.reason
                });
            }
        };

        var analyticsStep1Enhancement = {
            key: 'analytics-step-1',
            when: function (ctx) {
                return isFlowPage(ctx.flowRoot, { pageKey: 'reason_braverx' }) && !firedFlowEvents['step_1'];
            },
            run: function () {
                if (!window.BRXSessionAnalytics) return;
                var pushed = window.BRXSessionAnalytics.pushStep1();
                if (pushed) firedFlowEvents['step_1'] = true;
            }
        };

        var analyticsStep2Enhancement = {
            key: 'analytics-step-2',
            when: function (ctx) {
                return isFlowPage(ctx.flowRoot, { pageKey: 'ed_treatment' }) && !firedFlowEvents['step_2'];
            },
            run: function () {
                if (!window.BRXSessionAnalytics || !window.BRXSessionAnalytics.pushStep2) return;
                var pushed = window.BRXSessionAnalytics.pushStep2({ flowId: savvyId });
                if (pushed) firedFlowEvents['step_2'] = true;
            }
        };

        var analyticsStep3Enhancement = {
            key: 'analytics-step-3',
            when: function (ctx) {
                return isFlowPage(ctx.flowRoot, { pageKey: 'contact_info' }) && !firedFlowEvents['step_3'];
            },
            run: function () {
                if (!window.BRXSessionAnalytics || !window.BRXSessionAnalytics.pushStep3) return;
                var pushed = window.BRXSessionAnalytics.pushStep3({ flowId: savvyId });
                if (pushed) firedFlowEvents['step_3'] = true;
            }
        };

        var enhancements = [
            consentLinksEnhancement,
            contactInfoConsentEnhancement,
            checkoutReadyEnhancement,
            analyticsPageViewEnhancement,
            analyticsStep1Enhancement,
            analyticsStep2Enhancement,
            analyticsStep3Enhancement
        ];

        // --------------------------------------------------
        // Checkout-ready click interception
        // Remove once checkout destination can be controlled natively.
        // --------------------------------------------------

        function handleCheckoutReadyClick(event) {
            var target = event.target;
            if (!target || !target.closest) return;
            var button = target.closest('[data-component-key-value="go_to_checkout_button"]');
            if (!button) return;
            if (!sectionRoot || !sectionRoot.contains(button)) return;

            event.preventDefault();
            event.stopPropagation();

            var resolved = window.BRXSession && window.BRXSession.getResolvedCheckoutUrl
                ? window.BRXSession.getResolvedCheckoutUrl()
                : null;

            if (resolved && (resolved.status === 'valid' || resolved.status === 'patched')) {
                window.location.href = resolved.url;
                return;
            }

            console.warn('[Checkout Ready] No usable checkout URL.', resolved);
        }

        function start() {
            if (sectionRoot) {
                sectionRoot.addEventListener('click', handleCheckoutReadyClick);
            }

            if (!checkoutSessionUnsubscribe && window.BRXSession && window.BRXSession.subscribe) {
                checkoutSessionUnsubscribe = window.BRXSession.subscribe(function () {
                    if (!activeFlowRef.current) return;
                    applyCheckoutReadyButtonState(activeFlowRef.current);
                });
            }
        }

        function stop() {
            if (sectionRoot) {
                sectionRoot.removeEventListener('click', handleCheckoutReadyClick);
            }
            if (checkoutSessionUnsubscribe) {
                checkoutSessionUnsubscribe();
                checkoutSessionUnsubscribe = null;
            }
        }

        return {
            enhancements: enhancements,
            activeFlowRef: activeFlowRef,
            start: start,
            stop: stop
        };
    }

    window.BRXFlowEnhancements = { create: create };
})();
